import { atom } from "jotai";
import { apiService } from "@/api";
import type { CurrentProjectState } from "@/api/model/current-project-state";
import { TimeoutTimer } from "@/utils/timer";
import { DATA_FETCH_INTERVAL_MILLIS } from "./constants";
import { projectsAtom, selectedPropertyIdAtom } from "./property-domain";

export const currentProjectStatesAtom = atom<CurrentProjectState<{ project: string }>[]>([]);
currentProjectStatesAtom.debugLabel = "currentProjectStatesAtom";

export const currentProjectStatesLoadingAtom = atom<boolean>(false);
currentProjectStatesLoadingAtom.debugLabel = "currentProjectStatesLoadingAtom";
export const currentProjectStatesErrorAtom = atom<string | null>(null);
currentProjectStatesErrorAtom.debugLabel = "currentProjectStatesErrorAtom";

export const fetchCurrentProjectStatesAtom = atom(null, async (get, set) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  if (!selectedPropertyId) {
    // console.log("fetchCurrentProjectStatesAtom - No property ID selected");
    return;
  }

  // console.log(`fetchCurrentProjectStatesAtom - Fetching current project states for property ${selectedPropertyId}`);
  const currentProjectStates = await apiService.currentProjectState.getByProperty(selectedPropertyId, null);
  // console.log(`fetchCurrentProjectStatesAtom - Fetched current project states: ${currentProjectStates.length} items`);
  set(currentProjectStatesAtom, currentProjectStates);
});
fetchCurrentProjectStatesAtom.debugLabel = "fetchCurrentProjectStatesAtom";
fetchCurrentProjectStatesAtom.onMount = (dispatch) => {
  const timer = new TimeoutTimer();
  setTimeout(() => {
    dispatch();
  }, 1000);
  timer.start(DATA_FETCH_INTERVAL_MILLIS, async () => {
    await dispatch();
  });
  return () => {
    timer.stop();
  };
};

export const currentProjectStatesByPropertyAtom = atom<CurrentProjectState<{ project: string }>[]>((get) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  const currentProjectStates = get(currentProjectStatesAtom);

  if (!selectedPropertyId) {
    return [];
  }

  return currentProjectStates.filter((state) => {
    const project = state.project;
    const projects = get(projectsAtom);
    const projectObj = projects.find((p) => p.id === project);
    return projectObj?.property === selectedPropertyId;
  });
});
currentProjectStatesByPropertyAtom.debugLabel = "currentProjectStatesByPropertyAtom";

export const currentProjectStateByProjectIdAtom = atom(
  (get) =>
    (projectId: string): CurrentProjectState<{ project: string }> | null => {
      const currentProjectStates = get(currentProjectStatesAtom);
      return (
        currentProjectStates.find((state) => {
          const project = state.project;
          return project === projectId;
        }) || null
      );
    }
);
currentProjectStateByProjectIdAtom.debugLabel = "currentProjectStateByProjectIdAtom";

export const anyProjectRunningAtom = atom((get) => {
  const currentProjectStates = get(currentProjectStatesAtom);
  return currentProjectStates.some((state) => state.irrigation_status === "active");
});
anyProjectRunningAtom.debugLabel = "anyProjectRunningAtom";
